/**
 * AuthProvider — Magic-link (email OTP) auth via Zitadel Session API.
 *
 * When VITE_AUTH_DISABLED=true a fake user is injected and no login runs.
 * When VITE_AUTH_DISABLED is unset/false the magic-link flow is used:
 *   1. User enters email → POST /api/auth/magic-link/start
 *   2. User enters code → POST /api/auth/magic-link/verify → JWT + profile
 */

import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { setTokenGetter, setTenantIdGetter, adminListTenants } from "../api/client";
import type { AdminTenant } from "../api/types";
import { TenantPickerModal } from "../components/TenantPickerModal";

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  tenant_id: string;
  roles: string[];
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isPlatformAdmin: boolean;
  user: AuthUser | null;
  token: string | null;
  /** Tenant attivo selezionato dall'utente (o quello di default dal profilo). */
  activeTenant: string;
  /** Lista tenant disponibili (>1 solo per platform_admin). */
  tenantList: AdminTenant[];
  setActiveTenant: (tenantId: string) => void;
  login: () => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  isPlatformAdmin: false,
  user: null,
  token: null,
  activeTenant: "",
  tenantList: [],
  setActiveTenant: () => {},
  login: () => {},
  logout: () => {},
});

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";
const BASE = import.meta.env.VITE_API_BASE_URL || "";
const SESSION_KEY = "ts_auth";
const PENDING_OTP_KEY = "ts_pending_otp";
const CHOSEN_TENANT_KEY = "ts_chosen_tenant";

/* ── Mock user for development ── */
const MOCK_USER: AuthUser = {
  sub: "mock-operator-001",
  email: "operatore@casadei-test.local",
  name: "Operatore Test",
  tenant_id: "*",
  roles: ["platform_admin"],
};
const MOCK_TOKEN = "mock-jwt-token-for-development";

function MockAuthProvider({ children }: { children: React.ReactNode }) {
  // Fallback "dev" quando adminListTenants() non è raggiungibile (dev senza
  // backend): mantiene testabile il mock provider offline. In produzione
  // (MagicLinkAuthProvider) questo fallback non esiste: lì l'assenza di
  // backend è un errore reale, non un caso da coprire silenziosamente.
  const [activeTenant, setActiveTenantState] = useState<string>(() => loadChosenTenant() || "dev");
  const [tenantList, setTenantList] = useState<AdminTenant[]>([]);

  useEffect(() => {
    setTokenGetter(() => MOCK_TOKEN);
    // Sincronizza subito il getter con lo stato iniziale (fallback "dev"
    // incluso), altrimenti apiFetch userebbe l'header X-Tenant-ID stale
    // finché l'operatore non seleziona esplicitamente un tenant.
    setTenantIdGetter(() => activeTenant);
    adminListTenants()
      .then((list) => {
        setTenantList(list);
        // Non autoselezionare: il platform_admin sceglie via modale.
      })
      .catch(() => {
        // Backend non raggiungibile: resta sul fallback "dev" già impostato.
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setActiveTenant = useCallback((id: string) => {
    setActiveTenantState(id);
    setTenantIdGetter(() => id);
    saveChosenTenant(id);
  }, []);

  // Modale di selezione cliente per il mock platform_admin (nessun tenant ancora scelto).
  const showPicker = !activeTenant && tenantList.length > 0;

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: true,
      isLoading: false,
      isPlatformAdmin: MOCK_USER.roles.includes("platform_admin"),
      user: MOCK_USER,
      token: MOCK_TOKEN,
      activeTenant,
      tenantList,
      setActiveTenant,
      login: () => {},
      logout: () => {
        window.location.href = "/";
      },
    }),
    [activeTenant, tenantList, setActiveTenant],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showPicker && (
        <TenantPickerModal
          tenants={tenantList}
          onSelect={(id) => setActiveTenant(id)}
        />
      )}
    </AuthContext.Provider>
  );
}

/* ── Helpers ── */

interface StoredSession {
  token: string;
  user: AuthUser;
  exp: number; // unix seconds
}

function saveSession(s: StoredSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

function loadSession(): StoredSession | null {
  try {
    // Migrazione: se il token era in sessionStorage (versione precedente), spostalo
    const legacy = sessionStorage.getItem(SESSION_KEY);
    if (legacy) {
      localStorage.setItem(SESSION_KEY, legacy);
      sessionStorage.removeItem(SESSION_KEY);
    }
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s: StoredSession = JSON.parse(raw);
    if (s.exp * 1000 < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

/* ── Pending OTP: persisted in localStorage (cross-tab) so /auth/callback works ── */

function savePendingOtp(sessionId: string, sessionToken: string): void {
  localStorage.setItem(PENDING_OTP_KEY, JSON.stringify({ sessionId, sessionToken, ts: Date.now() }));
}

function loadPendingOtp(): { sessionId: string; sessionToken: string } | null {
  try {
    const raw = localStorage.getItem(PENDING_OTP_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 10 minutes
    if (Date.now() - (data.ts || 0) > 10 * 60 * 1000) {
      localStorage.removeItem(PENDING_OTP_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function clearPendingOtp(): void {
  localStorage.removeItem(PENDING_OTP_KEY);
}

/** Call verify API. Used by both the code form and the /auth/callback page. */
export async function verifyOtp(
  sessionId: string,
  sessionToken: string,
  code: string,
): Promise<{ token: string; user: AuthUser }> {
  const resp = await fetch(`${BASE}/api/auth/magic-link/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, session_token: sessionToken, code }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({ detail: "Errore verifica" }));
    throw new Error(data.detail || `Errore ${resp.status}`);
  }
  const data = await resp.json();
  clearPendingOtp();
  return { token: data.token, user: data.user as AuthUser };
}

/** Decode JWT payload without verification (for extracting exp). */
function jwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length !== 3) return {};
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

function defaultTenant(user: AuthUser): string {
  return (!user.tenant_id || user.tenant_id === "*") ? "" : user.tenant_id;
}

/**
 * Tenant scelto dal platform_admin via modale. Persistito in localStorage
 * (NON in ts_auth) così sopravvive ai refresh ma viene azzerato a logout.
 * Agli utenti di tenant normale non serve: hanno già tenant_id nel profilo.
 */
function loadChosenTenant(): string {
  try {
    return localStorage.getItem(CHOSEN_TENANT_KEY) || "";
  } catch {
    return "";
  }
}

function saveChosenTenant(id: string): void {
  try {
    if (id) localStorage.setItem(CHOSEN_TENANT_KEY, id);
    else localStorage.removeItem(CHOSEN_TENANT_KEY);
  } catch {
    /* ignore quota errors */
  }
}

function applyAuth(token: string, tenant: string): void {
  setTokenGetter(() => token);
  setTenantIdGetter(() => tenant || null);
}

/* ── Magic-link auth provider ── */
function MagicLinkAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [activeTenant, setActiveTenantState] = useState<string>("");
  const [tenantList, setTenantList] = useState<AdminTenant[]>([]);

  const isPlatformAdmin = user?.roles?.includes("platform_admin") ?? false;

  // Carica la lista tenant quando l'utente è platform_admin.
  // NON autoseleziona il primo: il platform_admin sceglie via modale.
  // Gli utenti di tenant normale hanno già activeTenant dal profilo (defaultTenant).
  useEffect(() => {
    if (!isPlatformAdmin || !token) return;
    adminListTenants()
      .then((list) => {
        setTenantList(list);
      })
      .catch(() => {});
  }, [isPlatformAdmin, token]);

  const setActiveTenant = useCallback((id: string) => {
    setActiveTenantState(id);
    setTenantIdGetter(() => id || null);
    // Persisti solo le scelte esplicite del platform_admin (utenti tenant hanno
    // già il tenant nel profilo, non serve). loadChosenTenant ripristina al refresh.
    if (isPlatformAdmin) saveChosenTenant(id);
  }, [isPlatformAdmin]);

  // On mount: check for existing session or magic-link in URL
  useEffect(() => {
    const stored = loadSession();
    if (stored) {
      // Platform_admin: ripristina il tenant scelto in precedenza (se presente),
      // altrimenti resta vuoto → la modale di selezione viene mostrata.
      // Utenti normali: usa il tenant del profilo.
      const profileTenant = defaultTenant(stored.user);
      const tenant = profileTenant || (stored.user.roles?.includes("platform_admin") ? loadChosenTenant() : "");
      setUser(stored.user);
      setToken(stored.token);
      setActiveTenantState(tenant);
      applyAuth(stored.token, tenant);
      setIsLoading(false);
      return;
    }

    // Magic link: /login?session_id=...&code=...
    const params = new URLSearchParams(window.location.search);
    const mlSessionId = params.get("session_id");
    const mlCode = params.get("code");
    if (mlSessionId && mlCode) {
      const pending = loadPendingOtp();
      const sessionToken = pending?.sessionId === mlSessionId ? pending.sessionToken : "";
      verifyOtp(mlSessionId, sessionToken, mlCode)
        .then((result) => {
          handleLoginSuccess(result.token, result.user);
          window.history.replaceState({}, "", window.location.pathname);
        })
        .catch(() => setShowLogin(true))
        .finally(() => setIsLoading(false));
      return;
    }

    setIsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = useCallback((newToken: string, newUser: AuthUser) => {
    const payload = jwtPayload(newToken);
    const exp = (payload.exp as number) || Math.floor(Date.now() / 1000) + 43200;
    // Platform_admin: se ha già scelto un cliente in passato su questo browser,
    // ripristina quella scelta (niente modale). Solo alla primissima scelta in
    // assoluto (nessun ts_chosen_tenant salvato) la modale viene mostrata.
    const profileTenant = defaultTenant(newUser);
    const tenant = profileTenant || (newUser.roles?.includes("platform_admin") ? loadChosenTenant() : "");
    setUser(newUser);
    setToken(newToken);
    setActiveTenantState(tenant);
    applyAuth(newToken, tenant);
    saveSession({ token: newToken, user: newUser, exp });
    setShowLogin(false);
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    // NB: NON cancelliamo ts_chosen_tenant — la scelta del cliente resta valida
    // anche dopo logout/nuovo login. Si cambia solo dal dropdown in sidebar.
    setUser(null);
    setToken(null);
    setActiveTenantState("");
    setTenantList([]);
    setTokenGetter(() => null);
    setTenantIdGetter(() => null);
    setShowLogin(true);
  }, []);

  // Listen for 401 from apiFetch → force re-login
  useEffect(() => {
    function onUnauthorized() {
      console.warn("[Auth] 401 ricevuto — sessione scaduta, redirect al login");
      handleLogout();
    }
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, [handleLogout]);

  // Proactive expiry: schedule logout slightly before JWT expires
  useEffect(() => {
    if (!token) return;
    const payload = jwtPayload(token);
    const exp = (payload.exp as number) || 0;
    if (!exp) return;
    const msUntilExpiry = exp * 1000 - Date.now();
    const timeout = Math.max(msUntilExpiry - 30_000, 0);
    const timer = setTimeout(() => {
      console.warn("[Auth] Token in scadenza — redirect al login");
      handleLogout();
    }, timeout);
    return () => clearTimeout(timer);
  }, [token, handleLogout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: !!user,
      isLoading,
      isPlatformAdmin,
      user,
      token,
      activeTenant,
      tenantList,
      setActiveTenant,
      login: () => setShowLogin(true),
      logout: handleLogout,
    }),
    [user, token, isLoading, isPlatformAdmin, activeTenant, tenantList, setActiveTenant, handleLogout],
  );

  if (showLogin && !user) {
    return (
      <AuthContext.Provider value={value}>
        <MagicLinkForm onSuccess={handleLoginSuccess} onCancel={() => setShowLogin(false)} />
      </AuthContext.Provider>
    );
  }

  // Platform_admin senza tenant ancora scelto: mostra modale di selezione cliente.
  // Gli utenti di tenant normale hanno activeTenant già impostato dal profilo.
  const showTenantPicker = !!user && isPlatformAdmin && !activeTenant && tenantList.length > 0;

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showTenantPicker && (
        <TenantPickerModal
          tenants={tenantList}
          onSelect={(id) => setActiveTenant(id)}
        />
      )}
    </AuthContext.Provider>
  );
}

/* ── Magic-link login form ── */

type MagicLinkStep = "email" | "code" | "sending" | "verifying";

interface MagicLinkFormProps {
  onSuccess: (token: string, user: AuthUser) => void;
  onCancel: () => void;
}

function MagicLinkForm({ onSuccess, onCancel }: MagicLinkFormProps) {
  const [step, setStep] = useState<MagicLinkStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [error, setError] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStep("sending");
    try {
      const resp = await fetch(`${BASE}/api/auth/magic-link/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Errore di rete" }));
        throw new Error(data.detail || `Errore ${resp.status}`);
      }
      const data = await resp.json();
      setSessionId(data.session_id);
      setSessionToken(data.session_token);
      savePendingOtp(data.session_id, data.session_token);
      setStep("code");
    } catch (err) {
      setError((err as Error).message);
      setStep("email");
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStep("verifying");
    try {
      const result = await verifyOtp(sessionId, sessionToken, code.trim());
      onSuccess(result.token, result.user);
    } catch (err) {
      setError((err as Error).message);
      setStep("code");
    }
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", fontFamily: "var(--font-family)",
      background: "var(--indaco-gray-100)",
    }}>
      <div style={{
        background: "var(--indaco-brand-dark)", borderRadius: "var(--border-radius-xl)",
        boxShadow: "0 4px 24px rgba(0,40,85,0.25)",
        padding: "2.5rem 2rem 2rem", width: "100%", maxWidth: 420,
        display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem",
      }}>
        {/* Brand */}
        <img src="/tinexta-logo-white.svg" alt="Tinexta InfoCert" style={{ height: 28 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/trusty-icon-white.svg" alt="" style={{ height: 32, width: 32 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "var(--indaco-white)", lineHeight: 1.2 }}>
              Trusty Sieve
            </div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(255,255,255,0.6)" }}>
              Gestione Documenti
            </div>
          </div>
        </div>

        <hr style={{ width: "100%", border: "none", borderTop: "1px solid rgba(255,255,255,0.15)", margin: "0.25rem 0" }} />

        {step === "email" || step === "sending" ? (
          <>
            <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: "var(--font-size-base)", textAlign: "center" }}>
              Inserisci la tua email per accedere
            </p>
            <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
              <input
                className="form-input"
                type="email"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={step === "sending"}
              />
              <button type="submit" style={{
                width: "100%", padding: "0.75rem", borderRadius: "var(--border-radius-lg)", border: "none",
                background: "var(--indaco-white)", color: "var(--indaco-brand-dark)", fontWeight: 600, fontSize: "var(--font-size-base)",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                opacity: step === "sending" || !email.trim() ? 0.6 : 1,
              }} disabled={step === "sending" || !email.trim()}>
                {step === "sending" ? (
                  <><span className="spinner" /> Invio in corso…</>
                ) : (
                  <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span> Invia link di accesso</>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", fontSize: "var(--font-size-base)", textAlign: "center" }}>
              Abbiamo inviato un codice a<br />
              <strong style={{ color: "var(--indaco-white)" }}>{email}</strong>
            </p>
            <form onSubmit={handleCodeSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
              <input
                className="form-input"
                type="text"
                placeholder="00000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                style={{ fontSize: "1.5rem", textAlign: "center", letterSpacing: "0.3em" }}
                required
                autoFocus
                maxLength={8}
                disabled={step === "verifying"}
              />
              <button type="submit" style={{
                width: "100%", padding: "0.75rem", borderRadius: "var(--border-radius-lg)", border: "none",
                background: "var(--indaco-white)", color: "var(--indaco-brand-dark)", fontWeight: 600, fontSize: "var(--font-size-base)",
                cursor: "pointer", opacity: step === "verifying" || code.length < 8 ? 0.6 : 1,
              }} disabled={step === "verifying" || code.length < 8}>
                {step === "verifying" ? (
                  <><span className="spinner" /> Verifica…</>
                ) : "Accedi"}
              </button>
              <button type="button" style={{
                width: "100%", padding: "0.5rem", borderRadius: "var(--border-radius-lg)", border: "none",
                background: "transparent", color: "rgba(255,255,255,0.7)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: "var(--font-size-sm)",
              }} onClick={() => { setStep("email"); setCode(""); setError(""); }}
                disabled={step === "verifying"}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
                Cambia email
              </button>
            </form>
          </>
        )}

        {error && (
          <div style={{
            color: "var(--indaco-white)", background: "rgba(255,255,255,0.1)",
            padding: "0.75rem 1rem", borderRadius: "var(--border-radius-lg)", fontSize: "var(--font-size-sm)",
            width: "100%", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>error</span>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: "0.5rem" }}>
          © Tinexta InfoCert 2026
        </div>
      </div>
    </div>
  );
}

/* ── Exported Provider — picks mock vs magic-link based on env ── */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (AUTH_DISABLED) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }
  return <MagicLinkAuthProvider>{children}</MagicLinkAuthProvider>;
}
